import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const uagcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const uagcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}];
interface UagcProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const uagcPrograms: UagcProgram[] = [
// Associate - Business
{
  shortTitle: 'A.A. - Business',
  longTitle: 'Associate of Arts (A.A.) in Business',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54516'
}, {
  shortTitle: 'A.A. - Organizational Management',
  longTitle: 'Associate of Arts (A.A.) in Organizational Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54517'
},
// Associate - Education
{
  shortTitle: 'A.A. - Early Childhood Education',
  longTitle: 'Associate of Arts (A.A.) in Early Childhood Education',
  degreeLevel: 'associate',
  areaOfStudy: 'education',
  mProgram: '1639'
},
// Associate - Liberal Arts
{
  shortTitle: 'A.A. - Military Studies',
  longTitle: 'Associate of Arts (A.A.) in Military Studies',
  degreeLevel: 'associate',
  areaOfStudy: 'liberal-arts',
  mProgram: '54693'
},
// Bachelor's - Business
{
  shortTitle: 'B.A. - Accounting',
  longTitle: 'Bachelor of Arts (B.A.) in Accounting',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1610'
}, {
  shortTitle: 'B.A. - Business Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1608'
}, {
  shortTitle: 'B.A. - Business Leadership',
  longTitle: 'Bachelor of Arts (B.A.) in Business Leadership',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1611'
}, {
  shortTitle: 'B.A. - Finance',
  longTitle: 'Bachelor of Arts (B.A.) in Finance',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1614'
}, {
  shortTitle: 'B.A. - Human Resources Management',
  longTitle: 'Bachelor of Arts (B.A.) in Human Resources Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1618'
}, {
  shortTitle: 'B.A. - Marketing',
  longTitle: 'Bachelor of Arts (B.A.) in Marketing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1619'
}, {
  shortTitle: 'B.A. - Organizational Management',
  longTitle: 'Bachelor of Arts (B.A.) in Organizational Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1621'
}, {
  shortTitle: 'B.A. - Project Management',
  longTitle: 'Bachelor of Arts (B.A.) in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1622'
},
// Bachelor's - Criminal Justice
{
  shortTitle: 'B.A. - Social and Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Social and Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '1642'
},
// Bachelor's - Education
{
  shortTitle: 'B.A. - Early Childhood Development w/ Differentiated Instruction',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Development w/ Differentiated Instruction',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1645'
}, {
  shortTitle: 'B.A. - Early Childhood Education',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1612'
}, {
  shortTitle: 'B.A. - Early Childhood Education Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1613'
}, {
  shortTitle: 'B.A. - Education Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Education Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1637'
}, {
  shortTitle: 'B.A. - Instructional Design',
  longTitle: 'Bachelor of Arts (B.A.) in Instructional Design',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1641'
},
// Bachelor's - Information Technology
{
  shortTitle: 'B.A. - Business Information Systems',
  longTitle: 'Bachelor of Arts (B.A.) in Business Information Systems',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1609'
}, {
  shortTitle: 'B.A. - Information Technology',
  longTitle: 'Bachelor of Arts (B.A.) in Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1625'
}, {
  shortTitle: 'B.S. - Computer Software Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Software Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1628'
}, {
  shortTitle: 'B.S. - Cyber & Data Security Technology',
  longTitle: 'Bachelor of Science (B.S.) in Cyber & Data Security Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1640'
},
// Bachelor's - Social & Behavioral Science
{
  shortTitle: 'B.A. - Applied Behavioral Science',
  longTitle: 'Bachelor of Arts (B.A.) in Applied Behavioral Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1643'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1671'
}, {
  shortTitle: 'B.A. - Sociology',
  longTitle: 'Bachelor of Arts (B.A.) in Sociology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1672'
}];
export function UAGCLandingPage() {
  usePageMeta('University of Arizona Global Campus | Online Degree Programs', 'Explore flexible online degree programs at The University of Arizona Global Campus. Accredited programs in Business, Criminal Justice, and Education designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    'social-behavioral-science': 'Social & Behavioral Science'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  // Get available areas based on selected degree level
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    uagcPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  // Get available programs based on selected area and degree level
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return uagcPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = uagcPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'The University of Arizona Global Campus',
            logo: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
            primaryColor: '#1C2B39',
            accentColor: '#CC0033',
            backUrl: '/colleges/university-of-arizona-global-campus',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2665" data-mp="227e3a0n8glr">
          <div data-id="element-2666" data-mp="3u833y84rc0ok">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2667" data-mp="3u7jlmqb73aa0">
              Degree Level
            </label>
            <div className="relative" data-id="element-2668" data-mp="3kbzs2ak3snof">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800" data-id="element-2669" data-mp="ls9olwm5wv93">
                <option value="" data-id="element-2670" data-mp="71sn6d9j25c7">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-2671" data-mp="zsalz237xkjt">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2672" data-mp="2u0xz1dif4g4b">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2673" data-mp="2y3kjuugo90s0" />
              </div>
            </div>
          </div>

          <div data-id="element-2674" data-mp="2p9g0sx7s4tn1">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2675" data-mp="2p8wgxaykjv6d">
              Area of Study
            </label>
            <div className="relative" data-id="element-2676" data-mp="2fcbaqmatce0y">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2677" data-mp="2f95kh3qg763n">
                <option value="" data-id="element-2678" data-mp="el1cpybxfn2f">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-2679" data-mp="ioh3wf588v7q">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2680" data-mp="xfgaxlqezbp7">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2681" data-mp="16e1qd80laalr" />
              </div>
            </div>
          </div>

          <div data-id="element-2682" data-mp="12czr4r6o9yjs">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2683" data-mp="12cgi5zz02khw">
              Program
            </label>
            <div className="relative" data-id="element-2684" data-mp="3454nqfz49k0u">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2685" data-mp="346632wn60sod">
                <option value="" data-id="element-2686" data-mp="39rzz1p30l2vk">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2687" data-mp="3nqg7plkft3gc">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2688" data-mp="12zph11uw8r4v">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2689" data-mp="1d3cuf6gg64wc" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-2690" data-mp="1841fm2zn59he">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2691" data-mp="zcr9lc7fnkvt">
          <div data-id="element-2692" data-mp="zbpyj7qf2ye3">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2693" data-mp="za54dj80lhgr">
              Selected Program
            </label>
            <div className="relative" data-id="element-2694" data-mp="keyz4ygqtmoh">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2695" data-mp="kg0al41o8b6q">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2696" data-mp="hceleqa5nn71">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2697" data-mp="8jkyc6ogvjim">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2698" data-mp="3lj5wv55i7uh" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2699" data-mp="3upzpjmy3lymh" />

          <div data-id="element-2700" data-mp="3pry7n5ces5cl">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2701" data-mp="3ps7vam9ze0f4">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2702" data-mp="3gue1pj3lpkvn">
              <option value="" data-id="element-2703" data-mp="1mims8wh6gxv1">-- Please Select --</option>
              {uagcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2704" data-mp="1waabwb7htaly">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2705" data-mp="13llasc6ft5a7">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2706" data-mp="18lhe097b8sop">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2707" data-mp="18l7v15konlsi">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2708" data-mp="1hj1idr0gz53j">
              <option value="" data-id="element-2709" data-mp="tv02vaia926p">-- Please Select --</option>
              {uagcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2710" data-mp="ovn8m4p30su3">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2711" data-mp="xx4uy4t31qr8">{errors.startDate}</p>}
          </div>

          <div data-id="element-2712" data-mp="a8tydduf6d8r">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2713" data-mp="a8btxtxlsspr">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2714" data-mp="jzoo4s6vjuyy">
              <option value="" data-id="element-2715" data-mp="3io5b8azre87v">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2716" data-mp="1y5ba51om5ts5">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2717" data-mp="22y4h7w7k1e7e">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2718" data-mp="1u3zhsoecuug9">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2719" data-mp="1u5127jh9gtan">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2720" data-mp="27ubaopirkx8g" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2721" data-mp="2cto5pxjsham4">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2722" data-mp="1bwo1g12yma7r">
          <div data-id="element-2723" data-mp="17wna5furi4ja">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2724" data-mp="17w42qt2qmyji">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2725" data-mp="1zobi7q6ic4wb" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2726" data-mp="a7aiew9e3kdu">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2727" data-mp="2u3q67nc9bm">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2728" data-mp="54jctgbb6cx2" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2729" data-mp="ev5nqx82yf9f">
                <div data-id="element-2730" data-mp="iw7ppltdt5f3">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2731" data-mp="iumwbziszsit">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="City" data-id="element-2732" data-mp="srj3o1wyu8ls" />
                </div>
                <div data-id="element-2733" data-mp="2ml2q8gtl2xfo">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2734" data-mp="2mlkqrpna3xte">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="State" data-id="element-2735" data-mp="2wktl4jm23ega" />
                </div>
              </div>
              <div data-id="element-2736" data-mp="3t9994b9ipfra">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2737" data-mp="3t6n68oxknofe">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`} placeholder="123 Main St" data-id="element-2738" data-mp="gpne6zt9lqfb" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2739" data-mp="3k5kp5az932ep">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2740" data-mp="367dxwdot40o0">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2741" data-mp="3b562o8ajvh9m">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2742" data-mp="3lm9mj1dmnase" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2743" data-mp="3hobwxxfy2moh">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2744" data-mp="2tx6edaimdgah">
          <div className="grid grid-cols-2 gap-3" data-id="element-2745" data-mp="2ty7qzjfaaaub">
            <div data-id="element-2746" data-mp="2oxtsarzkod8y">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2747" data-mp="2oyd55uad8bg4">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2748" data-mp="zfmzajip81js" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2749" data-mp="gq6uvl3w7oy3">{errors.firstName}</p>}
            </div>
            <div data-id="element-2750" data-mp="lp1n1dnr2puv">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2751" data-mp="loi5hy20i3hd">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2752" data-mp="uieoj3zvqzsk" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2753" data-mp="38sfy08toycu2">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2754" data-mp="2ywmc3yu316zm">
            <div data-id="element-2755" data-mp="2yvus7l9ukxp6">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2756" data-mp="2yygub5dyyl00">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2757" data-mp="2twrllwe7yjf3" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2758" data-mp="267ywojw0lbtz">{errors.email}</p>}
            </div>
            <div data-id="element-2759" data-mp="2b60b2rihwmz4">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2760" data-mp="2b8mo36o5nuoq">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2761" data-mp="21iwh5ixx5gef" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2762" data-mp="2k9xc7vvx4rsp">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#CC0033] focus:ring-[#CC0033] cursor-pointer flex-shrink-0" data-id="element-2763" data-mp="2fbm35ndg6lbj">
            <>
                By selecting Request Information, I consent to receive calls,
                emails, and texts from the University of Arizona Global Campus
                (UAGC) which may be sent using automated or prerecorded
                technology. Message and data rates may apply; frequency may
                vary. This consent is not a condition of applying, enrolling, or
                purchase. I may contact UAGC directly at 866-711-1700, I may opt
                out of SMS anytime by replying STOP, and can text HELP for
                assistance.{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/uwnAfhD-utqnhD___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6NWFlZDplNzRhOWMyMmMxZmRmOTJlOGI0ZmI1NTU3NjBmMWQxMDRlN2M5NzVmNmRhN2M2NDY5YWFlNmZhNWZjYzc1OGZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2764" data-mp="3p15jd6mtwogg">
                  Privacy Policy
                </a>
                ,{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/yjwrx-fsi-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6ODA3ZTowMTFlY2JiNTVhZjhmMjE3ODhjMGMwYThmZDczMmVhMTk2MGI3ZmUzZGNmYWQ2NGM1M2FlMDY3ZTBmNjAxM2RiOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2765" data-mp="1ibkfw2yz9553">
                  Terms and Conditions
                </a>{' '}
                of Usage, and{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/xrx-yjwrx-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6OWIyNDpmZmQyOTUwMDZmNzIwZTRkMjdkY2NmOWU1NjZhMWM3OGM3N2QyNGE2YmExYmViZmZkYWU1MjQ5NTJjNjA5MjZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2766" data-mp="pnev5g7xbg59">
                  SMS Terms and Conditions
                </a>{' '}
                apply.
            </>
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-2767" data-mp="2s9a5283j4bie">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2768" data-mp="2sabph36fqacs">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2769" data-mp="2n8mfzryv24qt" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#CC0033] to-[#A50028] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2770" data-mp="16umlwxmp59bh">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2771" data-mp="2vheml2ipnrw" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2772" data-mp="32d7eagwt6qnh">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2773" data-mp="3g4c5qvq0wcmq">
        <img src="https://cdn.magicpatterns.com/uploads/hZrXUkDbsWP7copst5KXLY/UAGC.png" alt="University of Arizona Global Campus Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-2774" data-mp="3bdvrxaiaiozu" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2A3F52] to-[#1A4D80] relative overflow-hidden" data-id="element-2775" data-mp="2jm7k91agiqnc">
        {/* Decorative image overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2776" data-mp="rkejzvlvd3nm" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-2777" data-mp="dnhvw1x4y0af">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-2778" data-mp="1ozsexgm2ui1o">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-2779" data-mp="1u183hingr6ot">
              University of Arizona Global Campus
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-2780" data-mp="39je1h7x3vdty">
              {currentProgram ? currentProgram.longTitle : <>
                  Flexible Online
                  <br data-id="element-2781" data-mp="2ut6tjm4q5d7i" />
                  Degree Programs
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-2782" data-mp="2qqa6nj85iuxr">
            <form onSubmit={e => e.preventDefault()} id="uagc-lead-form" data-id="element-2783" data-mp="k1x67d6yn2ou">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-2784" data-mp="f3lvz1cr6q63" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2785" data-mp="3p1p1zxh0ox1v" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2786" data-mp="3aaosrq8g4z4o">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2787" data-mp="31dxo4pftwbyh">
                <div className="bg-gradient-to-r from-[#CC0033] to-[#A50028] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2788" data-mp="368ux5wd3ylbd"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2789" data-mp="pn3ilxdgugdb">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2790" data-mp="ul54sr66uvt0">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-2791" data-mp="32t0gnudn2xh6">
            The University of Arizona Global Campus offers accredited online
            degree programs designed for working adults. With flexible
            scheduling, dedicated support, and a connection to the University of
            Arizona, we help you achieve your educational and career goals.
          </p>
        </div>
      </section>

      {/* Reasons to Choose UAGC */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-2792" data-mp="2jeaxhhdrt71l">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2793" data-mp="2edvm2vd1u9m9">
          <div className="mb-10" data-id="element-2794" data-mp="2egrj8hh2ou9n">
            <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2795" data-mp="2sdehqooobsho"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39]" data-id="element-2796" data-mp="2sbtmewujc5ir">
              Reasons to Choose UAGC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2797" data-mp="32497w451jc8e">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2798" data-mp="2x4wgr774zj00">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2799" data-mp="2x78ybj8zfrcj">
                <ClipboardCheckIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2800" data-mp="3bzt28m3yi2v1" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2801" data-mp="3719ag52olwur">
                No SAT, GMAT, GRE, or ACT Required
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2802" data-mp="3uqr0u927ejqg">
                Standardized test scores are not required for enrollment.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2803" data-mp="3lpj3pubfboxy">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2804" data-mp="3lrm5fblxv6ii">
                <ClockIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2805" data-mp="3brlfh6tkds5c" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2806" data-mp="3btpv2m0tsflg">
                5- to 6-week Classes*
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2807" data-mp="32z1omsjikrv2">
                Take one class at a time as a full-time student.
              </p>
            </div>
            <div data-id="element-2808" data-mp="2o43w9zod82dz">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2809" data-mp="2t141y9haz34k">
                <DollarSignIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2810" data-mp="29w8phkleskhy" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2811" data-mp="255sc20hl8yvl">
                $0 Application Fee
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2812" data-mp="2065zlt9l3mnu">
                Apply to UAGC risk-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose UAGC */}
      <section className="py-16 bg-gray-50" data-id="element-2813" data-mp="2sublijcdjhp9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2814" data-mp="2st0gyhb3u5x3">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-2815" data-mp="2nxl1xm1d23z3">
            <div className="flex-1" data-id="element-2816" data-mp="2iwdy1y2c9oxp">
              <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2817" data-mp="2ivc9cqseh3xi"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-8" data-id="element-2818" data-mp="2dyvk1t9gsjiu">
                Why Choose <span className="text-[#CC0033]" data-id="element-2819" data-mp="17ia3oqru9ys4">UAGC</span>
              </h2>
              <ul className="space-y-4" data-id="element-2820" data-mp="12j6xh304hmik">
                <li className="flex items-start" data-id="element-2821" data-mp="12i5nl1uuf62f">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2822" data-mp="3e6aiumjyyve2" />
                  <span className="text-gray-700" data-id="element-2823" data-mp="3j5dczbyevh79">
                    <strong data-id="element-2824" data-mp="3j654obow0us9">Transfer approved college credits</strong> towards
                    your bachelor's program*
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2825" data-mp="3svoszwvddgui">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2826" data-mp="3swhsqdvwv635" />
                  <span className="text-gray-700" data-id="element-2827" data-mp="1olwcpwriicw">
                    <strong data-id="element-2828" data-mp="5ppg3nhxkyje">
                      College credit for relevant work/life experience
                    </strong>{' '}
                    through Prior Learning Assessment & Experiential Learning
                    Credit Programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2829" data-mp="2chgf0qoo0fuh">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2830" data-mp="2ciriepe77ll2" />
                  <span className="text-gray-700" data-id="element-2831" data-mp="2hg9uuprfcixt">
                    <strong data-id="element-2832" data-mp="bqy3bf0ruu38">Attend for 3 weeks risk-free</strong> to see if UAGC
                    is right for you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2833" data-mp="6v0lt5drvhov">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2834" data-mp="kre60m9oxx71" />
                  <div className="text-gray-700" data-id="element-2835" data-mp="grvlhrls7o4q">
                    <strong data-id="element-2836" data-mp="gozny4duj1gt">
                      One course at a time in an accelerated format
                    </strong>
                    <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-600" data-id="element-2837" data-mp="3k82n7skmzbav">
                      <li data-id="element-2838" data-mp="1tjofjuhydd1">• 5 weeks for undergraduate degrees</li>
                      <li data-id="element-2839" data-mp="1uuqnf8and21">• 6 weeks for graduate programs</li>
                      <li data-id="element-2840" data-mp="3t7z4380sb5vd">• 9 weeks for doctoral dissertation courses</li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2841" data-mp="35gtsx65sh1re">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2842" data-mp="31hkuhnzt0fpi" />
                  <span className="text-gray-700" data-id="element-2843" data-mp="2rcl2xvn9p6g4">
                    <strong data-id="element-2844" data-mp="2rb1j5v2ekd9l">Access the online classroom 24/7</strong> so you can
                    study at night, on the weekends, or during lunch breaks
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2845" data-mp="2hcu4xjzi09ba">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2846" data-mp="2md8fp9shhxd0" />
                  <span className="text-gray-700" data-id="element-2847" data-mp="3a3cixg1rfv3h">
                    <strong data-id="element-2848" data-mp="3a1j6tl8doio3">Learn from expert faculty</strong> with real-world
                    experience in their respective fields
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2849" data-mp="304ws7e6nanui">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2850" data-mp="353zvou6z8m0d" />
                  <span className="text-gray-700" data-id="element-2851" data-mp="1oprktwvkk14k">
                    <strong data-id="element-2852" data-mp="1ooy90hhr6lfk">Join the Honors Program</strong> for high-achieving
                    bachelor's students
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-2853" data-mp="5u33yujxmmk">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop&q=80" alt="Student studying online" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-2854" data-mp="3rcoh0xacull2" />
            </div>
          </div>
        </div>
      </section>

      {/* Student Testimonials */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2855" data-mp="3clpf2540yvag">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2856" data-mp="38ldrajgd8eur">
          <div className="flex flex-col lg:flex-row gap-0 rounded-2xl overflow-hidden" data-id="element-2857" data-mp="33n3vq7qqbc8m">
            {/* Left intro */}
            <div className="bg-[#1C2B39] p-8 lg:p-12 flex flex-col justify-center lg:w-1/4" data-id="element-2858" data-mp="2yp2g5wsihv1w">
              <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" data-id="element-2859" data-mp="11w8z5dop790u">
                Student Experience
              </p>
              <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-2860" data-mp="wxp8x132nb2o">
                Praise for UAGC
              </h2>
              <p className="text-white/70 text-sm mt-3" data-id="element-2861" data-mp="19fi7d3lgx1ob">
                In their own words, why students love UAGC
              </p>
            </div>
            {/* Testimonial cards */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-0" data-id="element-2862" data-mp="1e8kuasvqpcvj">
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2863" data-mp="1j4jnquvxdl2t">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80" alt="Vicente Rocco Vargas" className="w-full h-full object-cover" data-id="element-2864" data-mp="lim9xashq3x7" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2865" data-mp="vi3c3i8j9zof"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2866" data-mp="26v5u8hqa6xap">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2867" data-mp="26x05f4br3b1l">
                    "I've tried to send so many people to UAGC because of how
                    well they've treated me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2868" data-mp="2rz81z3t8wgnu">
                    Vicente "Rocco" Vargas
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2869" data-mp="36pxb80n5blp2">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop&q=80" alt="Jessica Smith" className="w-full h-full object-cover" data-id="element-2870" data-mp="31vjg7zcebgkx" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2871" data-mp="3pmowugq5ucwa"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2872" data-mp="3kn3wq2z97yhz">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2873" data-mp="3klso9puv62kj">
                    "Life has totally changed because there were so many things
                    that came out of this experience for me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2874" data-mp="5slcefaugvc2">
                    Jessica Smith
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2875" data-mp="3s74scgfgwikn">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop&q=80" alt="Wendy Bosquez" className="w-full h-full object-cover" data-id="element-2876" data-mp="3s522bwebepfx" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2877" data-mp="3o62pzqq97qeg"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2878" data-mp="399bwfy39h8wr">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2879" data-mp="3e73rh0z7db56">
                    "I believe that my future, my destiny is now bigger than my
                    dreams ever since being a part of UAGC."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2880" data-mp="1cfgtugkt2hzq">
                    Wendy Bosquez
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-2881" data-mp="2mosmz7quqd1v">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2882" data-mp="2mnzpyyhp31wx">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2883" data-mp="2rh2ir4ene7c2"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-6" data-id="element-2884" data-mp="2wfdszg8uujut">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2885" data-mp="31cvyewnnygy2">
            UAGC maintains institutional accreditation and several programmatic
            accreditations, which means that it consistently meets quality
            standards that all students deserve.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-2886" data-mp="1b2kgqg6gl98z">
            So, whether you are <strong data-id="element-2887" data-mp="1g0lzt13vx8kg">transferring credits†</strong> you have
            earned from another institution, or you are just{' '}
            <strong data-id="element-2888" data-mp="18iefwhd55ywn">starting your college journey</strong>, you can rest assured
            knowing you are receiving the most for your investment at UAGC.
          </p>
        </div>
      </section>

      {/* Try Your First Course Free */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-2889" data-mp="ypgua2curttt">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2890" data-mp="ttrovie5jipl">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2891" data-mp="1rgqqm7jpbguc">
            Try Your First Course Complementary for 3 Weeks: No Cost, No Risk,
            Total Confidence
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10" data-id="element-2892" data-mp="1hhhzdmg80gbo">
            The UAGC Promise affords new degree-seeking students the opportunity
            to get a feel for the UAGC online platform before enrolling to make
            sure it's the right fit. With the UAGC Promise, attend for the first
            three weeks of your first course without any financial risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-2893" data-mp="3ipog0a508vg">
            <div className="flex flex-col items-center" data-id="element-2894" data-mp="3glmvozpnlyc">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2895" data-mp="8g80hzjgb47o">
                <ShieldCheckIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2896" data-mp="3pp4o84xu3wax" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2897" data-mp="3unfy2fo4q6t5">
                Attend for 3 Weeks Risk Complementary
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2898" data-mp="3lnsx1uzbb30m">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2899" data-mp="3lpdrlklmmlyh">
                <DollarSignIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2900" data-mp="36x258mxq03xi" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2901" data-mp="3byj4ke0k3ibz">
                Start School with No Financial Burden
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2902" data-mp="320lk81lti5on">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2903" data-mp="32269bbpd4wf4">
                <LaptopIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2904" data-mp="2jdmbme8h8lz6" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2905" data-mp="2eermyvntw375">
                Flexibility to Find Your Fit
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2906" data-mp="32anlaccz7a9g">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2907" data-mp="329ciaercu6je">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2908" data-mp="2xc2hk2yim8g9"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-2909" data-mp="2x9q1xwg8ca6d">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A843]" data-id="element-2910" data-mp="370uaoefbaysr">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-2911" data-mp="3l150rf8ss9cv">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#1C2B39] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-2912" data-mp="3l0byaqgvg60j">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-2913" data-mp="7xzui9hyefb3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-2914" data-mp="ctf5mtoktx3t">
          <p data-id="element-2915" data-mp="hpnnieu5s4eo">Certain degree programs may not be available in all states.</p>
          <p data-id="element-2916" data-mp="3h6c9dyg1u6ou">
            * Classes last 5 weeks for undergraduate programs, 6 weeks for
            graduate programs, and 9 weeks for doctoral capstone, planning and
            project classes.
          </p>
          <p data-id="element-2917" data-mp="3q57d93vmf4oz">
            † The transferability of credits is subject to the University of
            Arizona Global Campus transfer credit policies and requires the
            submission of official transcripts. The official transcripts will be
            evaluated to determine the credits that will officially apply toward
            a UAGC degree program.
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-2918" data-mp="2ux57xe6hsy5b">
            The University of Arizona Global Campus is accredited by the Higher
            Learning Commission (hlcommission.org), an institutional
            accreditation agency recognized by the U.S. Department of Education.
          </p>
          <LegalLinks className="pt-2" data-id="element-2919" data-mp="2g77otcnf6ymz" />
          <p className="text-gray-400" data-id="element-2920" data-mp="2g64yb5u0ey7l">
            © {new Date().getFullYear()} University of Arizona Global Campus.
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}